import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchExpenseByAny,
  selectCurrentExpense,
  deleteExpense,
} from "@/store/redux/slices/finance_management/expense_slice";
import {
  fetchAccountByAny,
  selectCurrentAccount,
} from "@/store/redux/slices/finance_management/account_slice";
import Back from "@/components/specific/Back";
import Button from "@/components/common/Button";
import { CopyOutlined } from "@/config/antd";
import { Tooltip, message, Switch } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import "@/assets/styles/custom_switch.css";
import DisputeDialog from "./components/Dispute_Dialog";
import WebIcon from "@/components/common/Web_Icons";
import accountCard from "@/assets/images/account_card.png";
import TransactionTable from "./components/Transaction_Table";
import Breadcrumb from "@/components/specific/Breadcrumb";
import Error from "@/components/common/alerts/Error";
import { getOrganizationId } from "@/utils/token_functions";

const SingleFinancialManager = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const expense = useSelector(selectCurrentExpense);
  const account = useSelector(selectCurrentAccount);
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [showDisputeModal, setShowDisputeModal] = useState(false);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Simple useEffect with async/await and try/catch - like SingleCampaign
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      showLoader();
      try {
        const expenseResult = await dispatch(
          fetchExpenseByAny({ key: "_id", value: id })
        ).unwrap();

        // After expense is fetched, get the account
        const debitingAccountID =
          expenseResult?.transactionChannel?.debitingAccountID;

        if (debitingAccountID) {
          await dispatch(
            fetchAccountByAny({
              key: "_id",
              value: debitingAccountID,
            })
          ).unwrap();
        } else {
          const organizationId = getOrganizationId();
          if (organizationId) {
            await dispatch(
              fetchAccountByAny({
                key: "organizationID",
                value: organizationId,
              })
            ).unwrap();
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setShowError(true);
        setErrorMessage(error?.message || "Failed to fetch data");
      } finally {
        hideLoader();
      }
    };

    fetchData();
  }, [dispatch, hideLoader, id, showLoader]);

  // Transform items data from expense
  const items =
    expense?.items?.map((item, index) => ({
      item: item?.item || `Item ${index + 1}`,
      quantity: item?.quantity || 0,
      totalPrice: (item?.price * item?.quantity)?.toLocaleString() || "0",
      price: item?.price?.toLocaleString() || "0",
    })) || [];

  // Transform transaction data from expense
  const transaction = expense
    ? {
        id: expense?.id || expense?._id,
        dateIssued: new Date().toLocaleDateString("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
        dueDate: "31 JUN 2023", // You might want to add this to your backend
        merchantName: expense?.merchantInformation?.name,
        merchantEmail: expense?.merchantInformation?.email,
        merchantAddress: expense?.merchantInformation?.location,
      }
    : {};

  // Transform payment details using account data
  const paymentDetails = {
    wireTransfer: {
      accountName: account?.bankName || "Loading...",
      accountNumber: account?.accountNumber || "Loading...",
      // routingNumber: account?.spendingLimit?.toLocaleString() || "Loading...",
      routingNumber: "No routing number available",
    },
    totals: {
      subTotal: expense?.grandTotal?.toLocaleString() || "0",
      discount: "10%",
      salesTax: "0",
      totalAmount:
        expense?.amount?.toLocaleString() ||
        expense?.grandTotal?.toLocaleString() ||
        "0",
    },
  };

  const handleDelete = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);
    showLoader();
    try {
      await dispatch(deleteExpense(expense?._id)).unwrap();
      hideLoader();
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error deleting expense:", error);
      hideLoader();
      setShowError(true);
      setErrorMessage(error?.message || "Failed to delete expense");
    }
  };

  const handleModify = useCallback(() => {
    showLoader();
    navigate(`/dashboard/financial/edit/${id}`);
    hideLoader();
  }, [id, navigate, showLoader, hideLoader]);

  const handleDispute = () => {
    setShowDisputeModal(true);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard!");
  };

  // breadcrumb items
  const breadCrumbItems = [
    { title: "Expense Management", link: "/dashboard/financial" },
    { title: "Campaign Materials", link: null },
  ];

  return (
    <div className="w-full text-left px-0 sm:px-2 md:px-4 lg:px-6">
      <Breadcrumb items={breadCrumbItems} />

      <div className="px-2 sm:px-3 lg:px-4">
        {/* Back Navigation */}
        <Back text="Back" />
      </div>

      <div className="grid-cols-3 gap-2 lg:gap-20 lg:grid">
        <div className="border-0 lg:border-2 border-[#eee] rounded-xl p-1 sm:p-2 lg:p-4 lg:mt-6 col-span-2">
          {/* Header Section */}
          <div className="mt-4 mb-4 lg:mt-0 px-2 sm:px-3 lg:px-0">
            <h1 className="heading-regular text-darkGrey">
              {expense?.name || "Loading..."}
            </h1>
            <p className="mt-2 text-regular text-darkGrey">
              {expense?.description || "Loading..."}
            </p>
          </div>

          {/* Transaction Details Card */}
          <div className="bg-primary mb-2.5 text-white p-2 sm:p-3 lg:p-6 rounded-md shadow-md lg:flex items-center justify-between mx-1 sm:mx-2 lg:mx-0">
            {/* Transaction Information */}
            <div className="mb-6 lg:mb-0">
              <div>
                <h3 className="mb-2 font-medium text-gray-300 text-small">
                  TRANSACTION #
                </h3>
                <div className="flex items-center mb-2.5">
                  <p className="font-medium preserve-text-white heading-large">
                    {transaction.id}
                  </p>
                  {/* <Tooltip title="Copy" placement="top">
                    <button
                      className="ml-2 text-white transition-colors hover:text-gray-300"
                      onClick={() => handleCopy(transaction.id)}
                    >
                      <CopyOutlined />
                    </button>
                  </Tooltip> */}
                </div>
              </div>

              <div className="mb-2.5">
                <p className="font-medium preserve-text-white text-small-xs">
                  <span className="mb-2 mr-2 font-medium text-gray-300">
                    DATE ISSUED
                  </span>
                  {transaction.dateIssued}
                </p>
              </div>

              <div className="mb-2.5">
                <p className="font-medium preserve-text-white text-small-xs">
                  <span className="mb-2 mr-2 font-medium text-gray-300">
                    DUE DATE
                  </span>
                  {transaction.dueDate}
                </p>
              </div>
            </div>

            {/* Merchant Information */}
            <div className="col-span-2 mb-2.5 text-left lg:text-right">
              <h3 className="mb-2 font-medium text-gray-300 text-small">
                MERCHANT&apos;S INFORMATION
              </h3>
              <p className="font-medium preserve-text-white text-bold">
                {transaction.merchantName}
              </p>
              <div className="flex items-center lg:justify-end gap-2 mt-2">
                <p className="text-sm font-bold preserve-text-white">
                  {transaction.merchantEmail}
                </p>
                <Tooltip title="Copy" placement="top">
                  <button
                    className="text-white transition-colors hover:text-gray-300"
                    onClick={() => handleCopy(transaction.merchantEmail)}>
                    <CopyOutlined />
                  </button>
                </Tooltip>
              </div>
              <p className="mt-2 text-sm text-gray-300">
                {transaction.merchantAddress}
              </p>
            </div>
          </div>

          {/* Items List Section */}
          <div className="mt-4 lg:mt-6 px-2 sm:px-3 lg:px-0">
            <h2 className="text-bold-x text-darkGrey">Item Details</h2>
            <h2 className="mb-2 lg:mb-4 text-regular text-darkGrey">
              Item list and their prices
            </h2>
            <div className="lg:border-2 border-[#eee] rounded-xl">
              <TransactionTable data={items} />
            </div>
          </div>

          {/* Payment Method Section */}
          <div className="mt-6 lg:mt-10 px-2 sm:px-3 lg:px-0">
            <h2 className="mb-2 lg:mb-4 text-bold text-darkGrey">
              Selected Payment Method
            </h2>
            <div className="p-2 sm:p-3 lg:p-6 mb-6 lg:mb-10 rounded-md shadow-sm bg-secondary">
              <div className="flex items-start justify-between mb-6">
                <h3 className="text-small text-darkGrey">WIRE TRANSFER</h3>
                <img
                  src={accountCard}
                  alt="Wise"
                  className="h-[40px] w-[60px]"
                />{" "}
                {/* Add your Wise logo */}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-small text-darkGrey">ACCOUNT NAME</span>
                  <span className="font-bold text-black text-small">
                    {paymentDetails.wireTransfer.accountName}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-small text-darkGrey">
                    ACCOUNT NUMBER
                  </span>
                  <span className="font-bold text-black text-small">
                    {paymentDetails.wireTransfer.accountNumber}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-small text-darkGrey">
                    ROUTING NUMBER
                  </span>
                  <span className="font-bold text-black text-small">
                    {paymentDetails.wireTransfer.routingNumber}
                  </span>
                </div>
              </div>
            </div>

            {/* Totals Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-bold text-black text-small">
                  Sub Total
                </span>
                <span className="font-bold text-black text-small">
                  ₦{paymentDetails.totals.subTotal}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-small text-darkGrey">Discount</span>
                <span className="text-small text-darkGrey">
                  {paymentDetails.totals.discount}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-small text-darkGrey">Sales Tax</span>
                <span className="text-small text-darkGrey">
                  ₦{paymentDetails.totals.salesTax}
                </span>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className="text-black text-bold">Total Amount</span>
                <span className="text-black text-bold">
                  ₦{paymentDetails.totals.totalAmount}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Initiator Section */}
        <div className="mt-6 lg:mt-0 lg:sticky top-[90px] lg:h-[90vh] px-2 sm:px-3 lg:px-0">
          {/* Profile Section */}
          <div className="flex flex-col items-center mb-10 lg:border-2 border-[#eee] rounded-xl p-4">
            <Avatar
              size={96}
              src={expense?.merchantInformation?.profileImage || null}
              alt={expense?.merchantInformation?.name || "Transaction Initiator"}
              className="mb-4"
            />
            <h2 className="mb-1 text-primary heading-regular">
              {expense?.merchantInformation?.name || "Loading..."}
            </h2>
            <p className="font-normal text-lighterGrey text-bold">
              Transaction Initiator
            </p>
          </div>

          {/* Amount Section */}
          <div className="mb-8 lg:border-2 border-[#eee] rounded-xl p-4">
            <div className="flex items-center justify-between mb-8">
              <span className="font-bold text-lighterGrey text-medium">
                TOTAL AMOUNT
              </span>
              <span className="font-normal text-lighterGrey text-medium">
                (NGN)
              </span>
            </div>
            <div className="flex items-baseline mb-2">
              <span className="font-bold heading-large text-darkGrey">
                ₦{paymentDetails?.totals?.subTotal}
              </span>
              <span className="ml-2 font-normal text-gray-400 text-small">
                (TAX INCL.)
              </span>
            </div>
            <div className="bg-[#FFF5F5] text-small-xs text-danger font-bold py-2 px-4 rounded-lg inline-block">
              DUE ON MAR. 29TH, 2023
            </div>
            {/* Toggle Section */}
            <div className="flex items-center justify-between">
              <span className="text-gray-700 text-small">
                Toggle to close this receipt
              </span>
              <Switch defaultChecked={false} />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between mb-8">
            <Button
              text="MODIFY"
              onClick={handleModify}
              icon={<WebIcon icon="BlueModify" size="small" />}
              iconPosition="left"
              customBgColor="white"
              customTextColor="primary"
              className="w-[185px] h-[36px] border border-primary mr-2 !bg-white font-normal hover:!bg-primary hover:!text-white"
            />
            <Button
              text="DELETE"
              onClick={handleDelete}
              icon={<WebIcon icon="BlueTrashFilled" size="small" />}
              iconPosition="left"
              customBgColor="white"
              customTextColor="primary"
              className="w-[185px] h-[36px] border border-primary mr-2 !bg-white font-normal hover:!bg-primary hover:!text-white"
            />
          </div>

          {/* Dispute Button */}
          <Button
            text="Dispute"
            onClick={handleDispute}
            color="primary"
            customBgColor="primary"
            customTextColor="white"
            className="w-full text-bold"
          />
        </div>
      </div>

      {/* Delete Modal */}
      <Delete
        isVisible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Transaction?"
        paragraph="Are you sure you want to delete this transaction?"
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />

      {/* Success Modal */}
      <Success
        isVisible={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate("/dashboard/financial");
        }}
        header="Transaction Deleted"
        paragraph="The transaction has been successfully deleted"
        buttonText="Continue"
      />

      {/* Error Dialog */}
      <Error
        isVisible={showError}
        onClose={() => setShowError(false)}
        header="Failed to Delete Transaction"
        paragraph={errorMessage}
        buttonText="Try Again"
      />

      {/* Dispute Modal */}
      <DisputeDialog
        isVisible={showDisputeModal}
        onClose={() => setShowDisputeModal(false)}
      />
    </div>
  );
};

export default SingleFinancialManager;
